import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { trackEvent, trackMetaEvent } from "@/components/Analytics";
import { FEATURE_FLAGS } from "@/config/featureFlags";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VEHICLE_OPTIONS = [
  "Toyota Hilux",
  "Ford Ranger",
  "VW Amarok",
  "Isuzu D-Max",
  "Mazda BT-50",
  "Nissan Navara",
  "Toyota Land Cruiser 79 Series",
  "Jeep Gladiator",
  "Holden Colorado",
  "JAC T9",
  "INEOS Quartermaster",
  "Land Rover Defender",
  "BYD Shark",
  "GWM Cannon",
  "Other / Not Listed",
];

const PRODUCT_OPTIONS = [
  "Wellside Canopy",
  "X-Series Tray & Canopy",
  "Bush Cabin Ute Camper",
  "Roof Tent",
  ...(FEATURE_FLAGS.HARD_LIDS_ENABLED ? ["Hard Lid (Tour Top Cover)"] : []),
  "XpeditioNZ Experience",
  "Dealer / Trade / Fleet Enquiry",
  "General Enquiry",
];

// One-tap starting points for the required Message field — reduces the
// "blank textarea" barrier for a hot lead who just wants a quick answer,
// without making the field optional (still gives our team a real question
// to respond to). Visitors can still edit/add to the text after picking one.
const QUICK_MESSAGE_CHIPS = [
  { label: "Get A Fitted Price", text: "Can you give me a price for my vehicle, fitted?" },
  { label: "Check Lead Time / Stock", text: "What's the current lead time, and is it in stock?" },
  { label: "Ask About An Option", text: "I have a question about one of the available options." },
];

// Lets another component (e.g. a "Become A Dealer" modal) preset the
// footer's Product Interest dropdown and jump straight to it — the footer
// enquiry form is a single instance mounted once in Layout.jsx, so a plain
// prop can't reach it after the fact; a window event can, regardless of
// when/whether the form has already mounted.
export const ENQUIRY_PRESET_EVENT = "xz-enquiry-preset";
export const goToEnquiryAs = (product) => {
  window.dispatchEvent(new CustomEvent(ENQUIRY_PRESET_EVENT, { detail: product }));
  // Scroll to the form itself (not just the section's top) so the preset
  // Product Interest dropdown is actually in view on mobile, instead of
  // landing on intro copy above the fold with the form still below it.
  const form = document.querySelector('[data-testid="footer-enquiry-form"]') || document.getElementById("enquire");
  form?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const EnquiryForm = ({ defaultProduct = "General Enquiry", idPrefix = "enquiry", productOptions = PRODUCT_OPTIONS }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    product: defaultProduct,
    message: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, error: null });

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, product: e.detail }));
    window.addEventListener(ENQUIRY_PRESET_EVENT, handler);
    return () => window.removeEventListener(ENQUIRY_PRESET_EVENT, handler);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: null });
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        vehicle: form.vehicle || null,
        product: form.product || null,
        message: form.message.trim(),
      };
      const { data } = await axios.post(`${API}/enquiry`, payload);
      trackEvent("enquiry_form_submit");
      trackMetaEvent("Lead", { content_name: payload.product || "General Enquiry" }, data.meta_event_id);
      setState({ loading: false, ok: true, error: null });
      setForm({ name: "", email: "", phone: "", vehicle: "", product: defaultProduct, message: "" });
      // eslint-disable-next-line no-console
      console.log("enquiry ok:", data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please email support@xpeditionz.co.nz directly.";
      setState({ loading: false, ok: false, error: detail });
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Name" required>
          <input
            type="text"
            required
            data-testid={`${idPrefix}-name`}
            value={form.name}
            onChange={update("name")}
            placeholder="Your full name"
            className="form-input"
          />
        </FormField>
        <FormField label="Email" required>
          <input
            type="email"
            required
            data-testid={`${idPrefix}-email`}
            value={form.email}
            onChange={update("email")}
            placeholder="you@example.com"
            className="form-input"
          />
        </FormField>
        <FormField label="Phone">
          <input
            type="tel"
            data-testid={`${idPrefix}-phone`}
            value={form.phone}
            onChange={update("phone")}
            placeholder="Optional"
            className="form-input"
          />
        </FormField>
        <FormField label="Vehicle">
          <select
            data-testid={`${idPrefix}-vehicle`}
            value={form.vehicle}
            onChange={update("vehicle")}
            className="form-input"
          >
            <option value="">Select vehicle (optional)</option>
            {VEHICLE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </FormField>
        <FormField label="Product Interest" className="md:col-span-2">
          <select
            data-testid={`${idPrefix}-product`}
            value={form.product}
            onChange={update("product")}
            className="form-input"
          >
            {!productOptions.includes(form.product) && form.product && (
              <option value={form.product}>{form.product}</option>
            )}
            {productOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </FormField>
        <FormField label="Message" required className="md:col-span-2">
          <div className="flex flex-wrap gap-2 mb-2" data-testid={`${idPrefix}-message-quick-chips`}>
            {QUICK_MESSAGE_CHIPS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                data-testid={`${idPrefix}-message-chip-${chip.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                onClick={() => setForm((f) => ({ ...f, message: chip.text }))}
                className="font-mono text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors"
              >
                {chip.label}
              </button>
            ))}
          </div>
          <textarea
            required
            rows={5}
            data-testid={`${idPrefix}-message`}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us about your build, timeline, or questions…"
            className="form-input resize-y"
          />
        </FormField>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <button
          type="submit"
          disabled={state.loading}
          data-testid={`${idPrefix}-submit`}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {state.loading ? (<><Loader2 size={14} className="animate-spin" /> Sending…</>) : (<>Send Enquiry <ArrowUpRight size={14} /></>)}
        </button>

        {state.ok && (
          <p data-testid={`${idPrefix}-success`} className="flex items-center gap-2 text-sm text-emerald-400">
            <CheckCircle2 size={16} /> Thanks — we'll be in touch shortly.
          </p>
        )}
        {state.error && (
          <p data-testid={`${idPrefix}-error`} className="flex items-center gap-2 text-sm text-red-400">
            <AlertCircle size={16} /> {state.error}
          </p>
        )}
      </div>
    </form>
  );
};

const FormField = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-tertiary)] mb-2">
      {label}{required && <span className="text-[var(--brand-primary)]"> *</span>}
    </span>
    {children}
  </label>
);
